import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Twitter, Linkedin, Link as LinkIcon, Check } from "lucide-react";
import Seo from "@/components/site/Seo";
import { Helmet } from "react-helmet-async";

/* ── Helpers ── */
const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
const initials = (name) =>
  (name || "").split(" ").filter(Boolean).map((s) => s[0]).join("").toUpperCase();
const slugify = (text) =>
  (text || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/* ── Structured data ── */
const ARTICLE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "The Best Superside Alternatives in 2026",
  datePublished: "2026-07-09",
  dateModified: "2026-07-09",
  author: { "@type": "Person", name: "Azam Tariq", url: "https://midcreatives.com/about" },
  publisher: { "@type": "Organization", name: "Midcreatives", url: "https://midcreatives.com" },
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    { "@type": "Question", name: "What is the best Superside alternative in 2026?", acceptedAnswer: { "@type": "Answer", text: "It depends on your size and budget. For companies at Series B and beyond that want senior designers and creative strategy, Midcreatives offers a dedicated team through an annual membership. For smaller teams that just need execution, ManyPixels or Design Pickle are cheaper options." } },
    { "@type": "Question", name: "Why are people leaving Superside in 2026?", acceptedAnswer: { "@type": "Answer", text: "In March 2026, Superside cut about 15% of its staff, mostly in project management and customer support. Prices went up to around $6,000 a month plus a $1,000 monthly platform fee. Many clients also complained about rotating designers and slow turnaround times." } },
    { "@type": "Question", name: "How much does Superside cost in 2026?", acceptedAnswer: { "@type": "Answer", text: "Superside starts at about $6,000 per month ($72,000 per year) plus a $1,000 monthly platform fee. Higher plans cost $9,000 to $12,000 per month, making the real annual cost between $84,000 and $156,000." } },
    { "@type": "Question", name: "What is a design membership?", acceptedAnswer: { "@type": "Answer", text: "A design membership is different from a subscription. With a subscription, you get access to a pool of rotating designers through a queue. With a membership like Midcreatives, you get the same dedicated team all year, creative direction on every project, and access to design leader sessions and a peer network." } },
  ],
};

/* ── Article content ── */
const POSTS = {
  "superside-alternatives": {
    slug: "superside-alternatives",
    category: "Design Industry",
    title: "The best Superside alternatives in 2026",
    subtitle: "Superside raised prices, cut staff, and pivoted to AI. An honest breakdown of every real alternative — and who each one is right for.",
    cover: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=1600&q=80",
    readTime: "9 min read",
    date: "2026-07-09",
    author: { name: "Azam Tariq", role: "Founder, Midcreatives" },
    seo: {
      title: "The Best Superside Alternatives in 2026 | Midcreatives",
      description: "Superside raised prices, cut staff, and pivoted to AI. An honest breakdown of every real alternative — and who each one is right for.",
    },
    toc: [
      "Why people are leaving Superside",
      "How we evaluated the alternatives",
      "The alternatives, evaluated",
      "Quick comparison",
      "Frequently asked questions",
    ],
    alternatives: [
      {
        name: "Midcreatives",
        fit: "Companies at Series B and beyond",
        price: "$60,000 / year",
        model: "Annual membership — dedicated senior team",
        works: ["Same senior team all year — no rotation", "Creative direction on every piece", "$20,000 project budget included", "Six design leader sessions per year", "Private network of peer brands", "Annual summit in Dubai"],
        doesnt: ["Higher price point — built for established companies", "Application required, limited seats", "Annual commitment only"],
        verdict: "If you have tried subscriptions and keep running into the same problems — rotating designers, slow queues, no strategic input — Midcreatives takes a different approach. You get a dedicated team that knows your brand, not a shared pool.",
        cta: true,
      },
      {
        name: "ManyPixels",
        fit: "Early-stage startups with simple recurring needs",
        price: "$549 – $1,299 / month",
        model: "Subscription queue — one request at a time",
        works: ["Low monthly cost", "Fast on simple requests", "Wide range of asset types"],
        doesnt: ["One active request at a time", "Designers rotate — no continuity", "No strategy or creative direction", "Quality varies at lower tiers"],
        verdict: "ManyPixels is a good fit for small teams that need basic assets produced quickly. It struggles when you need brand consistency or complex creative work.",
      },
      {
        name: "Design Pickle",
        fit: "Teams needing high volume of simple assets",
        price: "$499 – $1,695 / month",
        model: "Unlimited subscription queue",
        works: ["High output volume", "Dedicated designer on higher plans", "Predictable monthly cost"],
        doesnt: ["Quality ceiling is mid-level", "No brand strategy or direction", "Revisions slow everything down", "AI shift raising quality concerns"],
        verdict: "Design Pickle works when you need a lot of straightforward assets. For brand-level work or strategic creative, it falls short.",
      },
      {
        name: "Penji",
        fit: "Small businesses with basic design needs",
        price: "$499 – $999 / month",
        model: "Subscription queue",
        works: ["Simple to get started", "Reasonable speed", "Affordable entry point"],
        doesnt: ["Limited to basic work", "No senior talent", "Frequent quality complaints on review sites", "Not suitable for brand or product design"],
        verdict: "Penji is built for very small teams with simple recurring requests. It is not set up for companies with real brand requirements.",
      },
      {
        name: "A specialist design studio",
        fit: "Companies with one big project to complete",
        price: "$15,000 – $150,000+ per project",
        model: "One-time project scope",
        works: ["Deep expertise in one area", "Senior designers on the work", "Clear scope and timeline"],
        doesnt: ["No ongoing day-to-day support", "High cost per project", "Relationship ends when the project does"],
        verdict: "A good studio is often the right choice for a rebrand, a new website, or a design sprint. What it cannot do is support the daily creative work that happens the rest of the year.",
      },
    ],
    tableRows: [
      { name: "Midcreatives", price: "$60K / yr", senior: "Yes", dedicated: "Yes", strategy: "Yes", mark: true },
      { name: "Superside", price: "$72K – $130K / yr", senior: "Varies", dedicated: "No", strategy: "Limited" },
      { name: "ManyPixels", price: "$6.5K – $15K / yr", senior: "No", dedicated: "No", strategy: "No" },
      { name: "Design Pickle", price: "$6K – $20K / yr", senior: "No", dedicated: "Higher plans", strategy: "No" },
      { name: "Penji", price: "$6K – $12K / yr", senior: "No", dedicated: "No", strategy: "No" },
      { name: "Specialist studio", price: "Per project", senior: "Yes", dedicated: "Per project", strategy: "Yes" },
    ],
  },
};

/* ── Styles ── */
const bg = "#f5f1ea";
const ink = "#0a0a0a";
const muted = "rgba(10,10,10,0.65)";
const dim = "rgba(10,10,10,0.45)";
const line = "rgba(10,10,10,0.1)";
const cream = "#efeae0";

export default function BlogPost() {
  const { slug } = useParams();
  const [copied, setCopied] = useState(false);

  const post = POSTS[slug];
  if (!post) return <Navigate to="/blog" replace />;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {}
  };

  const shareX = () =>
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`, "_blank");
  const shareLI = () =>
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, "_blank");

  return (
    <div data-testid="blog-post" style={{ background: bg }}>
      <Seo title={post.seo.title} description={post.seo.description} path={`/blog/${post.slug}`} type="article" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(ARTICLE_SCHEMA)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>

      <article>
        {/* Header */}
        <header style={{ maxWidth: "1280px", margin: "0 auto", padding: "32px 40px 32px" }}>
          <Link to="/blog" style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "13px", color: dim, textDecoration: "none", marginBottom: "32px", fontFamily: "'Geist Mono', monospace" }}>
            <ArrowLeft size={14} /> Back to journal
          </Link>

          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "12px", color: dim, fontFamily: "'Geist Mono', monospace" }}>{post.category}</span>
            <span style={{ color: dim }}>·</span>
            <time style={{ fontSize: "12px", color: dim, fontFamily: "'Geist Mono', monospace" }}>{formatDate(post.date)}</time>
            <span style={{ color: dim }}>·</span>
            <span style={{ fontSize: "12px", color: dim, fontFamily: "'Geist Mono', monospace" }}>{post.readTime}</span>
          </div>

          <h1 className="font-display-tight" style={{ fontSize: "clamp(36px, 6vw, 72px)", color: ink, marginTop: "24px", lineHeight: 0.98, maxWidth: "900px", letterSpacing: "-0.025em" }}>
            {post.title}
          </h1>

          <p style={{ marginTop: "24px", fontSize: "18px", color: muted, maxWidth: "680px", lineHeight: 1.7 }}>
            {post.subtitle}
          </p>

          <div style={{ marginTop: "32px", display: "flex", alignItems: "center", gap: "16px" }}>
            <div style={{ width: "44px", height: "44px", borderRadius: "50%", background: ink, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontFamily: "'Geist Mono', monospace", flexShrink: 0 }}>
              {initials(post.author.name)}
            </div>
            <div>
              <p style={{ fontSize: "14px", color: ink, margin: 0, fontWeight: 500 }}>{post.author.name}</p>
              <p style={{ fontSize: "13px", color: dim, margin: 0 }}>{post.author.role}</p>
            </div>
          </div>
        </header>

        {/* Cover image */}
        <div style={{ maxWidth: "1440px", margin: "0 auto", padding: "0 40px 48px" }}>
          <div style={{ borderRadius: "24px", overflow: "hidden", border: `1px solid ${line}`, aspectRatio: "16/9" }}>
            <img src={post.cover} alt={post.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
        </div>

        {/* Key findings */}
        <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 40px 48px" }}>
          <div style={{ borderLeft: "3px solid #0a0a0a", padding: "24px 32px", background: cream, borderRadius: "0 8px 8px 0" }}>
            <p style={{ fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", color: dim, fontFamily: "'Geist Mono', monospace", marginBottom: "16px", marginTop: 0 }}>Key findings</p>
            {[
              "Superside now costs between $84,000 and $156,000 per year once the platform fee is included.",
              "The core problems — rotating designers, queues, no strategic direction — are built into the subscription model, not just Superside.",
              "Budget subscriptions are cheaper but trade those problems for lower quality output.",
              "For companies past early stage, the real choice is between a project studio or a dedicated membership.",
            ].map((line, i) => (
              <div key={i} style={{ display: "flex", gap: "12px", marginBottom: "10px", fontSize: "15px", color: ink, lineHeight: 1.6 }}>
                <span style={{ color: dim, flexShrink: 0, fontFamily: "'Geist Mono', monospace" }}>{i + 1}.</span>
                <span>{line}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Body + sidebar */}
        <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 40px 96px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: "64px", alignItems: "start" }}>

            {/* Article body */}
            <div>
              {/* Section 1 */}
              <section id={slugify("Why people are leaving Superside")} style={{ marginBottom: "56px", scrollMarginTop: "100px" }}>
                <h2 className="font-display-tight" style={{ fontSize: "clamp(24px, 3vw, 32px)", color: ink, marginBottom: "20px", lineHeight: 1.1, letterSpacing: "-0.02em" }}>
                  Why people are leaving Superside
                </h2>
                <p style={{ fontSize: "17px", color: muted, lineHeight: 1.85, marginBottom: "20px", maxWidth: "65ch" }}>
                  In March 2026, Superside laid off roughly 15% of its workforce. The cuts hit project management and customer support hardest — the two teams that clients work with every day.
                </p>
                <p style={{ fontSize: "17px", color: muted, lineHeight: 1.85, marginBottom: "20px", maxWidth: "65ch" }}>
                  At the same time, prices went up. Entry plans now start at around $6,000 a month, which is $72,000 a year. There is also a separate platform fee of $1,000 a month. That puts the real minimum cost at $84,000 a year before any work starts.
                </p>
                <p style={{ fontSize: "17px", color: muted, lineHeight: 1.85, marginBottom: "20px", maxWidth: "65ch" }}>
                  But the problems at Superside predate the layoffs. We read more than 1,200 verified reviews across Trustpilot, G2, and Clutch. The same seven complaints came up at every price point: rotating designers with no brand memory, a queue that slows everything down, no direct access to the designer, revision cycles that eat up time, all the briefing falling on the client, unclear pricing, and difficulty cancelling.
                </p>
                <p style={{ fontSize: "17px", color: muted, lineHeight: 1.85, maxWidth: "65ch" }}>
                  These are not problems that come from bad management. They are built into how the subscription model works. The client frustrations are a direct output of the model itself.
                </p>

                {/* Stat row */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", margin: "40px 0" }}>
                  {[
                    { num: "1,200+", label: "Reviews analysed across Trustpilot, G2, and Clutch" },
                    { num: "7", label: "Structural complaints at every price point" },
                    { num: "15%", label: "Staff cut by Superside in March 2026" },
                  ].map((s) => (
                    <div key={s.num} style={{ textAlign: "center", padding: "28px 20px", background: cream, borderRadius: "8px" }}>
                      <span className="font-display-tight" style={{ fontSize: "42px", fontWeight: 700, color: ink, lineHeight: 1, display: "block", marginBottom: "8px" }}>{s.num}</span>
                      <span style={{ fontSize: "13px", color: muted, lineHeight: 1.5 }}>{s.label}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* Section 2 */}
              <section id={slugify("How we evaluated the alternatives")} style={{ marginBottom: "56px", scrollMarginTop: "100px" }}>
                <h2 className="font-display-tight" style={{ fontSize: "clamp(24px, 3vw, 32px)", color: ink, marginBottom: "20px", lineHeight: 1.1, letterSpacing: "-0.02em" }}>
                  How we evaluated the alternatives
                </h2>
                <p style={{ fontSize: "17px", color: muted, lineHeight: 1.85, marginBottom: "20px", maxWidth: "65ch" }}>
                  We looked at six things for each option: the quality of the work they can produce, how the delivery model works day to day, who is doing the work and at what level, whether they offer any creative direction or just execution, how clearly they price things, and who each service is actually built for.
                </p>
                <p style={{ fontSize: "17px", color: muted, lineHeight: 1.85, maxWidth: "65ch" }}>
                  We did not rank by price. A $500-a-month service that works for a ten-person startup is not a worse option than a $60,000-a-year membership for a Series B company. They are for different buyers.
                </p>
              </section>

              {/* Section 3 — Alternatives */}
              <section id={slugify("The alternatives, evaluated")} style={{ marginBottom: "56px", scrollMarginTop: "100px" }}>
                <h2 className="font-display-tight" style={{ fontSize: "clamp(24px, 3vw, 32px)", color: ink, marginBottom: "32px", lineHeight: 1.1, letterSpacing: "-0.02em" }}>
                  The alternatives, evaluated
                </h2>

                {post.alternatives.map((alt, i) => (
                  <div key={alt.name} style={{ marginBottom: "40px", paddingBottom: "40px", borderBottom: `1px solid ${line}` }}>
                    <div style={{ display: "flex", alignItems: "baseline", gap: "12px", marginBottom: "4px" }}>
                      <span style={{ fontSize: "11px", color: dim, fontFamily: "'Geist Mono', monospace" }}>0{i + 1}</span>
                      <h3 className="font-display-tight" style={{ fontSize: "clamp(20px, 2.5vw, 26px)", color: ink, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0 }}>{alt.name}</h3>
                    </div>
                    <p style={{ fontSize: "12px", color: dim, fontFamily: "'Geist Mono', monospace", marginBottom: "16px", marginLeft: "28px" }}>Best for: {alt.fit}</p>

                    <div style={{ display: "flex", gap: "32px", padding: "12px 0", borderTop: `1px solid ${line}`, borderBottom: `1px solid ${line}`, marginBottom: "20px", marginLeft: "28px" }}>
                      <div>
                        <span style={{ fontSize: "11px", color: dim, fontFamily: "'Geist Mono', monospace", display: "block", marginBottom: "3px" }}>Price</span>
                        <span style={{ fontSize: "14px", color: ink }}>{alt.price}</span>
                      </div>
                      <div>
                        <span style={{ fontSize: "11px", color: dim, fontFamily: "'Geist Mono', monospace", display: "block", marginBottom: "3px" }}>Model</span>
                        <span style={{ fontSize: "14px", color: ink }}>{alt.model}</span>
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "20px", marginLeft: "28px" }}>
                      <div>
                        <span style={{ fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase", color: dim, fontFamily: "'Geist Mono', monospace", display: "block", marginBottom: "12px" }}>What works</span>
                        {alt.works.map((w) => (
                          <div key={w} style={{ display: "flex", gap: "8px", marginBottom: "8px", fontSize: "14px", color: muted, lineHeight: 1.5 }}>
                            <span style={{ color: "#0091ff", flexShrink: 0 }}>+</span>{w}
                          </div>
                        ))}
                      </div>
                      <div>
                        <span style={{ fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase", color: dim, fontFamily: "'Geist Mono', monospace", display: "block", marginBottom: "12px" }}>What does not</span>
                        {alt.doesnt.map((d) => (
                          <div key={d} style={{ display: "flex", gap: "8px", marginBottom: "8px", fontSize: "14px", color: muted, lineHeight: 1.5 }}>
                            <span style={{ color: dim, flexShrink: 0 }}>–</span>{d}
                          </div>
                        ))}
                      </div>
                    </div>

                    <blockquote style={{ borderLeft: "3px solid #e0ddd8", paddingLeft: "20px", marginLeft: "28px", fontStyle: "italic", fontSize: "16px", color: ink, lineHeight: 1.7, margin: "20px 0 20px 28px" }}>
                      {alt.verdict}
                    </blockquote>

                    {alt.cta && (
                      <div style={{ marginLeft: "28px" }}>
                        <Link to="/apply" className="btn-dark">
                          Apply for membership <span className="arrow-slide">→</span>
                        </Link>
                      </div>
                    )}
                  </div>
                ))}
              </section>

              {/* Section 4 — Table */}
              <section id={slugify("Quick comparison")} style={{ marginBottom: "56px", scrollMarginTop: "100px" }}>
                <p style={{ fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", color: dim, fontFamily: "'Geist Mono', monospace", marginBottom: "8px" }}>Exhibit 1</p>
                <div style={{ height: "1px", background: line, marginBottom: "16px" }} />
                <p style={{ fontSize: "16px", fontWeight: 600, color: ink, marginBottom: "24px", lineHeight: 1.4 }}>How the main options compare across the factors that matter most.</p>

                <div style={{ background: "#0d1117", borderRadius: "12px", padding: "32px", overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {["", "Annual price", "Senior talent", "Dedicated team", "Strategy"].map((h, i) => (
                          <th key={i} style={{ textAlign: "left", padding: "0 12px 16px 0", fontSize: "11px", letterSpacing: "0.08em", textTransform: "uppercase", color: "#8b8b8b", fontFamily: "'Geist Mono', monospace", fontWeight: "normal", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {post.tableRows.map((row) => (
                        <tr key={row.name}>
                          <td style={{ padding: "14px 12px 14px 0", fontSize: "14px", color: row.mark ? "#ffffff" : "#c9c9c9", fontWeight: row.mark ? 600 : 400, borderBottom: "1px solid rgba(255,255,255,0.06)", whiteSpace: "nowrap" }}>
                            {row.mark && <span style={{ display: "inline-block", width: "6px", height: "6px", background: "#0091ff", borderRadius: "50%", marginRight: "8px", verticalAlign: "middle" }} />}
                            {row.name}
                          </td>
                          {[row.price, row.senior, row.dedicated, row.strategy].map((val, j) => (
                            <td key={j} style={{ padding: "14px 12px 14px 0", fontSize: "14px", color: val === "Yes" ? "#6ee7b7" : val === "No" ? "#555" : "#c9c9c9", borderBottom: "1px solid rgba(255,255,255,0.06)", verticalAlign: "top", lineHeight: 1.4 }}>{val}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Section 5 — FAQ */}
              <section id={slugify("Frequently asked questions")} style={{ marginBottom: "56px", scrollMarginTop: "100px" }}>
                <h2 className="font-display-tight" style={{ fontSize: "clamp(24px, 3vw, 32px)", color: ink, marginBottom: "32px", lineHeight: 1.1, letterSpacing: "-0.02em" }}>
                  Frequently asked questions
                </h2>
                <div style={{ borderTop: `1px solid ${line}` }}>
                  {FAQ_SCHEMA.mainEntity.map((faq) => (
                    <div key={faq.name} style={{ padding: "24px 0", borderBottom: `1px solid ${line}` }}>
                      <h3 style={{ fontSize: "16px", fontWeight: 600, color: ink, marginBottom: "10px", fontFamily: "'Geist', sans-serif" }}>{faq.name}</h3>
                      <p style={{ fontSize: "15px", color: muted, lineHeight: 1.75, margin: 0 }}>{faq.acceptedAnswer.text}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Share row */}
              <div style={{ marginTop: "64px", paddingTop: "32px", borderTop: `1px solid ${line}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "16px" }}>
                <p style={{ fontSize: "12px", color: dim, fontFamily: "'Geist Mono', monospace", margin: 0 }}>Share this essay</p>
                <div style={{ display: "flex", gap: "8px" }}>
                  {[
                    { icon: <Twitter size={15} />, action: shareX, label: "share-twitter" },
                    { icon: <Linkedin size={15} />, action: shareLI, label: "share-linkedin" },
                    { icon: copied ? <Check size={15} /> : <LinkIcon size={15} />, action: copyLink, label: "share-copy" },
                  ].map((btn) => (
                    <button key={btn.label} onClick={btn.action} style={{ width: "40px", height: "40px", borderRadius: "50%", border: `1px solid ${line}`, background: "transparent", color: ink, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}>
                      {btn.icon}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside style={{ position: "sticky", top: "100px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

                {/* TOC */}
                <div style={{ borderRadius: "16px", border: `1px solid ${line}`, background: "#ffffff", padding: "24px" }}>
                  <p style={{ fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", color: dim, fontFamily: "'Geist Mono', monospace", marginBottom: "16px", marginTop: 0 }}>In this essay</p>
                  <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "12px" }}>
                    {post.toc.map((t, i) => (
                      <li key={t} style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                        <span style={{ fontSize: "11px", color: dim, fontFamily: "'Geist Mono', monospace", flexShrink: 0, marginTop: "2px" }}>{String(i + 1).padStart(2, "0")}</span>
                        <a href={`#${slugify(t)}`} style={{ fontSize: "13px", color: ink, textDecoration: "none", lineHeight: 1.5 }}>{t}</a>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Membership CTA */}
                <div style={{ borderRadius: "16px", background: cream, padding: "24px" }}>
                  <p style={{ fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", color: dim, fontFamily: "'Geist Mono', monospace", marginBottom: "12px", marginTop: 0 }}>Midcreatives Membership</p>
                  <h4 className="font-display-tight" style={{ fontSize: "20px", color: ink, lineHeight: 1.2, marginBottom: "10px" }}>
                    A senior team, a project budget, and a room of peers.
                  </h4>
                  <p style={{ fontSize: "13px", color: muted, lineHeight: 1.65, marginBottom: "16px" }}>
                    Everything we write about — put to work on your brand every business day.
                  </p>
                  <Link to="/apply" style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "10px 16px", borderRadius: "999px", background: ink, color: "#ffffff", fontSize: "13px", fontWeight: 500, textDecoration: "none" }}>
                    Get Membership →
                  </Link>
                </div>

                {/* Newsletter */}
                <div style={{ borderRadius: "16px", border: `1px solid ${line}`, background: "#ffffff", padding: "24px" }}>
                  <p style={{ fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", color: dim, fontFamily: "'Geist Mono', monospace", marginBottom: "12px", marginTop: 0 }}>Field notes · Monthly</p>
                  <h4 className="font-display-tight" style={{ fontSize: "18px", color: ink, lineHeight: 1.2, marginBottom: "16px" }}>One long-form essay a month.</h4>
                  <form onSubmit={(e) => e.preventDefault()} style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <input type="email" placeholder="you@company.com" style={{ padding: "10px 14px", borderRadius: "999px", border: `1px solid ${line}`, fontSize: "13px", color: ink, outline: "none", fontFamily: "inherit" }} />
                    <button type="submit" style={{ padding: "10px 14px", borderRadius: "999px", background: ink, color: "#ffffff", fontSize: "13px", fontWeight: 500, border: "none", cursor: "pointer", fontFamily: "inherit" }}>Subscribe</button>
                  </form>
                </div>

              </div>
            </aside>
          </div>
        </div>
      </article>
    </div>
  );
}
